import { useEffect, useState } from 'react';
import { api, ApiError, type ModelsSettings } from '../lib/api.js';
import { currentBrand } from '../lib/brand.js';

export function ModelsPage(): JSX.Element {
  const [data, setData] = useState<ModelsSettings | null>(null);
  const [primary, setPrimary] = useState('');
  const [backup, setBackup] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  async function load(refresh = false): Promise<void> {
    setBusy(true);
    setError(null);
    setSavedMsg(null);
    try {
      const res = refresh ? await api.refreshModels() : await api.getModelsSettings();
      setData(res);
      setPrimary(res.primary);
      setBackup(res.backup);
      if (res.fetchError) {
        setError(`Model list refresh failed (you can still type/save ids): ${res.fetchError}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      if (err instanceof ApiError && err.status === 401) return;
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load(false);
  }, []);

  async function onSave(): Promise<void> {
    const p = primary.trim();
    const b = backup.trim();
    if (!p || !b) {
      setError('Primary and backup model ids are required.');
      return;
    }
    setBusy(true);
    setError(null);
    setSavedMsg(null);
    try {
      const res = await api.saveModelsSettings(p, b);
      setData(res);
      setPrimary(res.primary);
      setBackup(res.backup);
      setSavedMsg('Saved. Backup is used if the primary fails (rate limit / overload).');
      if (res.fetchError) {
        setError(`Saved, but model list refresh failed: ${res.fetchError}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const models = data?.models ?? [];
  const modelIds = new Set(models.map((m) => m.id));
  // Allow current values even if not in the live list (typed / custom ids).
  const primaryOptions = [...models];
  if (primary && !modelIds.has(primary)) {
    primaryOptions.unshift({ id: primary, displayName: `${primary} (custom)` });
  }
  const backupOptions = [...models];
  if (backup && !modelIds.has(backup)) {
    backupOptions.unshift({ id: backup, displayName: `${backup} (custom)` });
  }

  return (
    <div>
      <h1 className="page-title">Models</h1>
      <p className="page-subtitle">
        Choose the primary Claude model for summaries and Ask Claude. If the primary fails
        (rate limit, overload, or temporary errors), {currentBrand().name} retries once with the backup.
      </p>

      {error && (
        <div className="form-error" style={{ marginBottom: 14 }}>
          {error}
        </div>
      )}
      {savedMsg && (
        <div className="panel" style={{ marginBottom: 14, padding: 12 }}>
          <p className="panel__subtle" style={{ margin: 0 }}>{savedMsg}</p>
        </div>
      )}

      <div className="panel">
        <h3 className="panel__title">Claude routing</h3>

        <div className="form-row">
          <label className="form-label" htmlFor="primaryModel">Primary model</label>
          <select
            id="primaryModel"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            disabled={busy}
          >
            {primaryOptions.length === 0 && (
              <option value={primary || ''}>{primary || 'No models loaded'}</option>
            )}
            {primaryOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName ? `${m.displayName} (${m.id})` : m.id}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Or type a model id"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            disabled={busy}
            style={{ marginTop: 8 }}
          />
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="backupModel">Backup model</label>
          <select
            id="backupModel"
            value={backup}
            onChange={(e) => setBackup(e.target.value)}
            disabled={busy}
          >
            {backupOptions.length === 0 && (
              <option value={backup || ''}>{backup || 'No models loaded'}</option>
            )}
            {backupOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName ? `${m.displayName} (${m.id})` : m.id}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Or type a model id"
            value={backup}
            onChange={(e) => setBackup(e.target.value)}
            disabled={busy}
            style={{ marginTop: 8 }}
          />
        </div>

        <p className="panel__subtle" style={{ marginBottom: 12 }}>
          {data?.refreshedAt
            ? `Model list last refreshed ${new Date(data.refreshedAt).toLocaleString()}.`
            : 'Model list not yet refreshed from Anthropic.'}
          {' '}
          {models.length > 0 ? `${models.length} Claude models available.` : ''}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="btn" disabled={busy} onClick={() => void onSave()}>
            Save
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => void load(true)}
          >
            Refresh list
          </button>
        </div>
      </div>
    </div>
  );
}
